import { useCallback, useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { useQueryStates } from 'nuqs';

import { tradingJournalFiltersParser } from '@/shared/lib/url-parsers';
import type { RangeValueType } from '@/shared/types/antd';

const DATE_FORMAT = 'YYYY-MM-DD';

const getDefaultDateRange = (): RangeValueType<DateString> => [
    dayjs().add(-30, 'd').format(DATE_FORMAT),
    dayjs().format(DATE_FORMAT),
];

export const useTradingJournalDateRange = () => {
    const [urlFilters, setUrlFilters] = useQueryStates(
        tradingJournalFiltersParser,
        {
            clearOnDefault: false,
            history: 'replace',
        },
    );

    const isInitialized = useRef(false);

    const hasUrlParams = Boolean(urlFilters.startDate && urlFilters.endDate);

    const dateRange = useMemo<RangeValueType<DateString>>(() => {
        const { startDate, endDate } = urlFilters;

        if (startDate && endDate) {
            return [startDate, endDate];
        }

        return getDefaultDateRange();
    }, [urlFilters]);

    useEffect(() => {
        if (!isInitialized.current && !hasUrlParams) {
            const defaultRange = getDefaultDateRange();

            setUrlFilters({
                startDate: defaultRange[0] ?? null,
                endDate: defaultRange[1] ?? null,
            });
        }

        isInitialized.current = true;
    }, [hasUrlParams, setUrlFilters]);

    const setDateRange = useCallback(
        (range: RangeValueType<DateString> | null) => {
            if (range) {
                const [startDate, endDate] = range;

                setUrlFilters({
                    startDate: startDate ?? null,
                    endDate: endDate ?? null,
                });
            } else {
                setUrlFilters({
                    startDate: null,
                    endDate: null,
                });
            }
        },
        [setUrlFilters],
    );

    const clearDateRange = useCallback(() => {
        setUrlFilters({
            startDate: null,
            endDate: null,
        });
    }, [setUrlFilters]);

    return {
        dateRange,
        setDateRange,
        clearDateRange,
    };
};
